import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface SliderQuestionItemState {
  /** Current slider value (0-100). */
  value: number;
  /**
   * Value at the time of submission. Retained (including for predictions) so a
   * later step can compare the learner's answer against a final result.
   */
  submittedValue?: number;
  /** Whether the question has been submitted. */
  submitted: boolean;
  /** Number of submissions made. */
  attempts: number;
}

export type SliderQuestionSliceState = Record<string, SliderQuestionItemState>;

const getDefaultState = (): SliderQuestionItemState => ({
  value: 0,
  submitted: false,
  attempts: 0,
});

const sliderQuestionSlice = createSlice({
  name: "sliderQuestion",
  initialState: {} as SliderQuestionSliceState,
  reducers: {
    setValue: (
      state,
      action: PayloadAction<{ componentID: string; value: number }>
    ) => {
      const { componentID, value } = action.payload;
      if (!state[componentID]) {
        state[componentID] = getDefaultState();
      }
      state[componentID].value = value;
      // Moving the slider starts a new answer, so the feedback from the previous
      // submission is cleared: it graded a value the learner has since changed,
      // and leaving it up pairs a verdict with a position it was not authored
      // for. `submittedValue` is kept so a later step can still compare against
      // the submitted answer, and `attempts` so the attempt limit cannot be
      // shed by nudging the slider.
      state[componentID].submitted = false;
    },
    submit: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      const current = state[componentID] || getDefaultState();
      state[componentID] = {
        ...current,
        submittedValue: current.value,
        submitted: true,
        attempts: current.attempts + 1,
      };
    },
    reset: (state, action: PayloadAction<string>) => {
      const componentID = action.payload;
      state[componentID] = getDefaultState();
    },
  },
});

// Internal — consumed by the useSliderQuestion hook only.
export const sliderQuestionActions = sliderQuestionSlice.actions;

// Exported for app-level store composition.
export const sliderQuestionReducer = sliderQuestionSlice.reducer;

export const selectSliderQuestionState = (
  state: { sliderQuestion: SliderQuestionSliceState },
  componentId: string
): SliderQuestionItemState =>
  state.sliderQuestion?.[componentId] || getDefaultState();

export type { SliderQuestionItemState };
